import {
  BayLcgVcgTcgAndPairings,
  BaySizesAndCgs,
  IBayPattern,
  TGetContainersCgsResult,
  extractContainersFromBayLevelSlots,
  getAllThePairedBays,
  getContainersCgs,
} from "@tedivo/tedivo-bay-grid-pure";
import {
  BayLevelEnum,
  IBayLevelData,
  IOpenVesselDefinitionV1,
  IShipData,
  ISizeSummary,
} from "open-vessel-definition";
import {
  IVesselOneParametrizationFormElementsTdvFrm,
  createVesselOneFormFields,
  vesselOneFormValidator,
} from "./create3DViewForm";
import {
  InputWithUnits,
  TedivoForm,
  createInputWithUnits,
  translateTedivoForm,
} from "@tedivo/tedivo-form";
import {
  assertNonNullish,
  createDictionary,
  pad2,
  pad3,
  roundDec,
} from "@tedivo/tedivo-pure-helpers";
import {
  getMeasuresFromSvgFileAttributes,
  getSideViewSvgElement,
} from "../../render/createSideView";
import ovdJsonStore, {
  ITvdCloudFileMeta,
} from "../../../../../app/stores/OVDJsonStore";

import FieldsValuesStore from "../../../../../app/stores/FieldsValuesStore";
import { IBayLevelOffsetBottomBase } from "open-vessel-definition";
import { IVesselOneParametrizationFormElements } from "@tedivo/tedivo-3d-visualizer";
import { IVesselParts } from "open-vessel-definition";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button";
import { getTranslation } from "../../../../../app/i18/i18tn";
import globalUnits from "../../../../../app/units/globalUnits";
import goSquared from "../../../../../app/tracking/goSquared";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import { useSimpleSideView } from "@tedivo/tedivo-bay-grid-core";

export function create3DViewEdit({
  sizeSummary,
  shipData,
  v3DParams,
  baysData,
  vesselPartsData,
  lcgVcgTcgAndPairings,
  submitButton,
}: {
  sizeSummary: ISizeSummary;
  shipData: IShipData;
  vesselPartsData: IVesselParts[];
  v3DParams: ITvdCloudFileMeta["v3DParams"] | undefined;
  baysData: IBayLevelData[];
  lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings;
  submitButton: SlButton;
}): IFormReturn<IVesselOneParametrizationFormElementsTdvFrm> | null {
  goSquared.addEvent("Edit-OVD - Edit Vessel Parts");
  let sideViewNode: SVGElement | null = null;

  const DEFAULT_PARAMS: IVesselOneParametrizationFormElements = {
    labels: {
      labelVesselName: shipData.shipName || "",
      labelCountry: "",
      labelIMOCode: shipData.imoCode || "",
      labelLateral: shipData.lineOperator,
    },
    colors: {
      mainColor: "#7384a2",
      bridgeColor: "#cccccc",
      bottomColor: "#b5736e",
      cranesColor: "#464b59",
      smokeBottomColor: "#cccccc",
      smokeTopColor: "#333333",
      separatorColor: "#700000",
      belowSeparatorColor: "#b8b4a9",
      labelsNoseColor: "#dddddd",
      labelsTailColor: "#dddddd",
      labelsSideColor: "#eeeeee",
      labelsBaysColor: "#cccccc",
    },
    tailOptions: {
      propellers: 1,
      rudders: 1,
    },
  };

  const holder = document.createElement("div");
  holder.className = "sections-oss-card";

  let containersWithPositions = ovdJsonStore.containersWithPositions;

  // 1. Bottom bases Data Store
  const dataStoreForBottomBases = new FieldsValuesStore();

  let panelInputs: HTMLDivElement | undefined = undefined;
  let svgGroupActive: SVGGElement | null = null;
  const unitsDisplayFn = globalUnits.lengthUnits.fromValueToFormattedDisplay;

  dataStoreForBottomBases.onChange = async (newValues, oldValues) => {
    const hasChanged = !bottomBasesMapsAreEqual(newValues, oldValues);
    if (hasChanged) updateAllSideView();
  };

  // 1. Editor of the parts
  const editorComp = document.createElement("tvd-vesselparts-editor-component");
  editorComp.style.position = "relative";

  editorComp.onApplyChanges = () => {
    const vesselPartsData = editorComp.getVesselParts() || [];

    const lcgVcgTcgAndPairings = new BayLcgVcgTcgAndPairings({
      ...ovdJsonStore.currentJson,
      vesselPartsData,
    } as IOpenVesselDefinitionV1);

    const extractedContainers =
      extractContainersFromBayLevelSlots(lcgVcgTcgAndPairings);

    containersWithPositions = getContainersCgs(
      lcgVcgTcgAndPairings,
      extractedContainers,
      { verticalSeparation: 0 },
      ovdJsonStore.currentJson?.shipData.masterCGs?.offsetBottomBases || [],
    );

    updateAllSideView();
  };

  editorComp.setData({
    vesselPartsData: vesselPartsData || [],
    bls: baysData,
    lcgVcgTcgAndPairings,
    offsetBottomBases:
      ovdJsonStore.currentJson?.shipData.masterCGs?.offsetBottomBases,
  });

  const editorSection = document.createElement("section");
  editorSection.appendChild(editorComp);

  const sideViewSection = document.createElement("section");
  sideViewSection.className = "side-view-section";

  const sideViewBottomBases = document.createElement("div");
  sideViewBottomBases.className = "side-view-details-holder";
  const sideViewSvg = document.createElement("div");
  sideViewSvg.className = "side-view-svg-holder";

  // 2. Form
  const formFields = createVesselOneFormFields({
    labels: {
      ...DEFAULT_PARAMS.labels,
      ...v3DParams?.params?.labels,
    },
    colors: {
      ...DEFAULT_PARAMS.colors,
      ...v3DParams?.params?.colors,
    },
    tailOptions: {
      ...DEFAULT_PARAMS.tailOptions,
      ...v3DParams?.params?.tailOptions,
    },
  });

  const tedivoForm =
    new TedivoForm<IVesselOneParametrizationFormElementsTdvFrm>({
      fields: formFields,
      onSubmit: () => undefined,
      formValidator: vesselOneFormValidator,
      submitButton: submitButton,
    });

  translateTedivoForm<IVesselOneParametrizationFormElementsTdvFrm>({
    tedivoForm,
    getTranslation: getTranslation,
  });

  // Append to holder
  holder.appendChild(editorSection);

  if (lcgVcgTcgAndPairings.cgStats.totalSlotsCount > 0) {
    if (lcgVcgTcgAndPairings.missingImportantVcgs) {
      sideViewSection.appendChild(sideViewBottomBases);
    }

    sideViewSection.appendChild(sideViewSvg);
    holder.appendChild(sideViewSection);
  }

  holder.appendChild(tedivoForm.form);

  return {
    node: holder,
    tedivoForm,
    submitFunction: submitPassedToEditDrawer,
  };

  async function submitPassedToEditDrawer() {
    const validResult = tedivoForm.doSubmitForm();

    if (validResult.success) {
      goSquared.addEvent("Edit-OVD - Edit Vessel Parts - Save");

      const result: IVesselOneParametrizationFormElements = {
        labels: {
          labelVesselName: validResult.data.labelVesselName,
          labelCountry: validResult.data.labelCountry,
          labelIMOCode: validResult.data.labelIMOCode,
          labelLateral: validResult.data.labelLateral,
        },
        colors: {
          mainColor: validResult.data.mainColor,
          bridgeColor: validResult.data.bridgeColor,
          bottomColor: validResult.data.bottomColor,
          cranesColor: validResult.data.cranesColor,
          smokeBottomColor: validResult.data.smokeBottomColor,
          smokeTopColor: validResult.data.smokeTopColor,
          separatorColor: validResult.data.separatorColor,
          belowSeparatorColor: validResult.data.belowSeparatorColor,
          labelsNoseColor: validResult.data.labelsNoseColor,
          labelsTailColor: validResult.data.labelsTailColor,
          labelsSideColor: validResult.data.labelsSideColor,
          labelsBaysColor: validResult.data.labelsBaysColor,
        },
        tailOptions: {
          propellers: validResult.data.propellers,
          rudders: validResult.data.rudders,
        },
      };

      const json = ovdJsonStore.currentJson;
      if (!json) return false;

      const canHaveOffsetBottomBases =
        lcgVcgTcgAndPairings.missingImportantVcgs;

      const vesselPartsData = editorComp.getVesselParts() || [];
      const offsetBottomBases = canHaveOffsetBottomBases
        ? calculateOffsetBottomBases(
            dataStoreForBottomBases.values as Map<string, number>,
          )
        : [];

      ovdJsonStore
        .setJson(ovdJsonStore.internalName || "", {
          ...json,
          shipData: {
            ...json.shipData,
            masterCGs: {
              ...json.shipData.masterCGs,
              offsetBottomBases,
            },
          },
          vesselPartsData,
        } as IOpenVesselDefinitionV1)

        .set3DViewParams(
          result,
          editorComp.getVesselParts(),
          offsetBottomBases.map((bb) => ({
            ...bb,
            bottomBase: bb.offset,
          })),
        );

      if (ovdJsonStore.lcgVcgTcgAndPairings) {
        const extractedContainers = extractContainersFromBayLevelSlots(
          ovdJsonStore.lcgVcgTcgAndPairings,
        );

        containersWithPositions = getContainersCgs(
          lcgVcgTcgAndPairings,
          extractedContainers,
          { verticalSeparation: 0 },
          ovdJsonStore.currentJson?.shipData.masterCGs?.offsetBottomBases || [],
        );
      }

      ovdJsonStore.containersWithPositions = containersWithPositions;

      return true;
    } else {
      return false;
    }
  }

  async function updateAllSideView() {
    console.time("Update Editing Side View");
    removeChildren(sideViewSvg);

    // 0. Prepare the data for the side view
    const vesselPartsData = editorComp.getVesselParts() || [];

    const lcgVcgTcgAndPairings = new BayLcgVcgTcgAndPairings({
      ...ovdJsonStore.currentJson,
      vesselPartsData: vesselPartsData,
    } as IOpenVesselDefinitionV1);

    // 1. Create the inputs for the bottom bases
    if (lcgVcgTcgAndPairings.missingImportantVcgs) {
      if (!panelInputs) {
        panelInputs = createBottomBasesInputsForEditing({
          bls: baysData,
          offsetBottomBases:
            ovdJsonStore.currentJson?.shipData.masterCGs?.offsetBottomBases,
          lcgVcgTcgAndPairings,
          dataStore: dataStoreForBottomBases,
          onInputFocus,
        });
        sideViewBottomBases.appendChild(panelInputs);
      }
    }

    // 2. Calculate the adjusted bottom bases.
    //    We do it after the inputs are created because the inputs set the values in the dataStore
    const offsetBottomBases = calculateOffsetBottomBases(
      dataStoreForBottomBases.values as Map<string, number>,
    );

    dataStoreForBottomBases.updatePrevValues();

    assertNonNullish(
      containersWithPositions,
      "containersWithPositions is null",
    );

    // 3. Create the side view SVG for editing
    sideViewNode = await createSideSvgViewForEditing({
      sizeSummary,
      baysData,
      vesselPartsData,
      lcgVcgTcgAndPairings,
      dataStore: dataStoreForBottomBases,
      offsetBottomBases: offsetBottomBases.map((bb) => ({
        ...bb,
        bottomBase: bb.offset,
      })),
      containersWithPositions,
      v3DParams,
      onHoverSvgGroup,
      unitsDisplayFn,
    });

    sideViewSvg.appendChild(sideViewNode);

    console.timeEnd("Update Editing Side View");
  }

  function onHoverSvgGroup(dataId: string) {
    const dataIdN = `b${dataId.replace("|", "_")}`;

    const slInputRef = sideViewBottomBases.querySelector(
      `#${dataIdN}`,
    ) as InputWithUnits<unknown> | null;

    if (!slInputRef) return;

    slInputRef.scrollIntoView({
      block: "nearest",
      inline: "nearest",
      behavior: "smooth",
    });
    slInputRef.focus();
    slInputRef.select();
  }

  function onInputFocus(e: Event) {
    const input = e.target as InputWithUnits<unknown>;
    const id = (input.getAttribute("id") || "")
      .replace("b", "")
      .replace("_", "|");
    const svgGroup = sideViewSvg?.querySelector(
      `g.svg-group-bay-level[data-id="${id}"]`,
    );

    if (svgGroupActive) {
      svgGroupActive.removeAttribute("data-active");
    }

    if (svgGroup) {
      svgGroupActive = svgGroup as SVGGElement;
      svgGroupActive.setAttribute("data-active", "true");
    }
  }
}

function calculateOffsetBottomBases(
  values: Map<string, number>,
): IBayLevelOffsetBottomBase[] {
  const allKeys = Array.from(values.keys()).sort();
  const newAdjustedBottomBases: IBayLevelOffsetBottomBase[] = [];

  allKeys.forEach((key) => {
    const [baysStr, level] = key.split("|");
    const bays = baysStr.split("-") as IBayPattern[];
    const value = values.get(key) as number | undefined;
    if (value === undefined) return;
    newAdjustedBottomBases.push({
      bays,
      level: parseInt(level),
      offset: value,
    });
  });

  return newAdjustedBottomBases;
}

async function createSideSvgViewForEditing({
  sizeSummary,
  baysData,
  lcgVcgTcgAndPairings,
  dataStore,
  vesselPartsData = [],
  offsetBottomBases,
  containersWithPositions,
  v3DParams,
  onHoverSvgGroup,
  unitsDisplayFn,
}: {
  sizeSummary: ISizeSummary;
  baysData: IBayLevelData[];
  lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings;
  dataStore: FieldsValuesStore;
  vesselPartsData?: IVesselParts[];
  offsetBottomBases: Array<IBayLevelOffsetBottomBase>;
  containersWithPositions: TGetContainersCgsResult;
  v3DParams?: ITvdCloudFileMeta["v3DParams"];
  onHoverSvgGroup: (dataId: string) => void;
  unitsDisplayFn: (value: number) => string;
}) {
  const root = getComputedStyle(document.body);

  const symbolsOptions = {
    strokeWidth: 4,
    strokeColor: root.getPropertyValue("--sl-color-neutral-400"),
    fontColor: root.getPropertyValue("--sl-color-neutral-700"),
    fillColor: root.getPropertyValue("--sl-color-neutral-50"),
    shipStrokeColor: root.getPropertyValue("--sl-color-primary-300"),
    shipTopColor: v3DParams?.params?.colors?.mainColor,
    shipBottomColor: v3DParams?.params?.colors?.bottomColor,
    pairedNoneContainerColor: root.getPropertyValue("--sl-color-violet-400"),
    pairedAftContainerColor: root.getPropertyValue("--sl-color-teal-400"),
    pairedFwdContainerColor: root.getPropertyValue("--sl-color-neutral-400"),
    warningColor: root.getPropertyValue("--sl-color-warning-700"),
    skyColor: root.getPropertyValue("--sl-color-neutral-0"),
  };

  const onDragColor = root.getPropertyValue("--sl-color-success-500");

  const svgSvg = await getSideViewSvgElement();
  const svgElementMeasures = getMeasuresFromSvgFileAttributes(
    svgSvg,
    "#SideView",
    {
      paddingShip: { x: 0, y: 150, z: 100 },
      paddingContArea: { x: 0, y: 0, z: 20 },
    },
  );

  const { sideViewSvg, svgSizeRatio } = useSimpleSideView({
    sizeSummary,
    baysData,
    lcgVcgTcgAndPairings,
    symbolsOptions,
    vesselPartsData,
    offsetBottomBases,
    selectContainerCriteria: "maxSize",
    containersWithPositions,
    svgElement: svgSvg,
    svgElementMeasures,
    prefixSymbol: "sideViewEdit",
  });

  /** Add handlers to move up and down bays if missing BottomBases (vcgs) */
  if (lcgVcgTcgAndPairings.missingImportantVcgs) {
    sideViewSvg.classList.add("sideview-action-move");

    const showSizeInfo = addDiffInfoInSvg(sideViewSvg);

    /** Bay Level G elements that hold containers in each bay and level */
    const svgBayLevelGroups = sideViewSvg.querySelectorAll(
      "g.svg-group-bay-level",
    ) as NodeListOf<SVGGElement>;

    if (svgBayLevelGroups.length === 0) {
      return sideViewSvg;
    }

    // Add the event to the parent node of the svg groups for HOVER on each group
    const svgBays = svgBayLevelGroups[0].parentNode;
    assertNonNullish(svgBays, "svgBays is null");

    let isDragging = false;
    let prevGHighlighted: SVGGElement | null = null;
    svgBays.addEventListener("mouseover", (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (isDragging) return;

      const target = e.target as SVGGElement;
      if (target.tagName !== "g" && target.tagName !== "use") return;

      const t = target.tagName === "use" ? target.parentElement : target;
      if (!t) return;

      const dSetId = t.dataset.id;

      onHoverSvgGroup(dSetId || "");
      if (prevGHighlighted) {
        if (prevGHighlighted === t) return;

        prevGHighlighted.removeAttribute("data-active");
      }

      prevGHighlighted = t as SVGGElement;

      if (t.getAttribute("data-active")) return;
      t.setAttribute("data-active", "true");
    });

    /** Dictionary of adjusted bottom bases */
    const adjustBB = offsetBottomBases.reduce((acc, bb) => {
      const key = `${bb.bays.join("-")}|${bb.level}`;
      acc[key] = String(bb.offset);
      return acc;
    }, {} as Record<string, string>);

    svgBayLevelGroups.forEach((svgGBayLevelGroup) => {
      const id = svgGBayLevelGroup.getAttribute("data-id");
      if (!id) return;

      svgGBayLevelGroup.dataset.initialY = adjustBB[id] || "0";

      attachUpAndDownDraggingActions({
        node: svgGBayLevelGroup,
        svgSizeRatio,
        onDragColor,
        showSizeInfo,
        onDraggingChange: (isDrg) => {
          isDragging = isDrg;
        },
        updateCb: (key, diffOverallY, initialY) => {
          // Set the new value
          const diffYInMm = roundDec(
            -diffOverallY / svgSizeRatio + Number(initialY),
            0,
          );

          //#region Help with the other level (Above->Below or Below->Above)
          const [baysStr, levelStr] = key.split("|");
          const level = Number(levelStr) as BayLevelEnum;
          const otherLevelKey = `${baysStr}|${
            level === BayLevelEnum.ABOVE
              ? BayLevelEnum.BELOW
              : BayLevelEnum.ABOVE
          }`;

          /** Opposite (above/Below) bay <g> */
          const otherG = sideViewSvg.querySelector(
            `g.svg-group-bay-level[data-id="${otherLevelKey}"]`,
          );

          dataStore.setValue(key, diffYInMm);

          if (!otherG) {
            dataStore.executeChange();
            return;
          }

          const otherGBBase = Number(otherG.getAttribute("data-bbase") || "0");
          const otherGAdjust =
            (dataStore.getValue(otherLevelKey) as number) || 0;

          const ownGBBase = Number(
            svgGBayLevelGroup.getAttribute("data-bbase") || "0",
          );

          if (level === BayLevelEnum.ABOVE) {
            const otherGHeight = Number(
              otherG.getAttribute("data-height") || "0",
            );

            const effectiveAboveBottom = ownGBBase + diffYInMm;
            const effectiveBelowTop = otherGBBase + otherGAdjust + otherGHeight;

            if (effectiveAboveBottom > effectiveBelowTop) {
              // Do nothing, the other level is already below
              dataStore.executeChange();
            } else {
              dataStore
                .setValue(
                  otherLevelKey,
                  effectiveAboveBottom - otherGHeight - 1000,
                )
                .executeChange();
            }
          } else {
            const effectiveAboveBottom = otherGBBase + otherGAdjust;
            const ownHeight = Number(
              svgGBayLevelGroup.getAttribute("data-height") || "0",
            );
            const effectiveBelowTop = ownGBBase + diffYInMm + ownHeight;

            if (effectiveAboveBottom > effectiveBelowTop) {
              // Do nothing, the other level is already above
              dataStore.executeChange();
            } else {
              dataStore
                .setValue(
                  otherLevelKey,
                  effectiveBelowTop + 1000 - otherGBBase, // 1m more for the other level (to have a gap)
                )
                .executeChange();
            }
          }
        },
      });
    });
  }

  return sideViewSvg;

  // Add the svg to the holder
  function addDiffInfoInSvg(svg: SVGElement) {
    const svgShowingDiff = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "g",
      ),
      svgShowingDiffBg = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "rect",
      ),
      svgShowingDiffText = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "text",
      );

    svgShowingDiff.setAttribute("data-fn", "showingDiff");
    svgShowingDiff.appendChild(svgShowingDiffBg);
    svgShowingDiff.appendChild(svgShowingDiffText);

    svgShowingDiffBg.setAttribute("x", "0");
    svgShowingDiffBg.setAttribute("y", "0");
    svgShowingDiffBg.setAttribute("width", "400");
    svgShowingDiffBg.setAttribute("height", "100");
    svgShowingDiffBg.setAttribute("fill", "black");

    svgShowingDiffText.setAttribute("x", "200");
    svgShowingDiffText.setAttribute("y", "50");
    svgShowingDiffText.setAttribute("font-size", "46px");
    svgShowingDiffText.setAttribute("fill", "white");
    svgShowingDiffText.setAttribute("text-anchor", "middle");
    svgShowingDiffText.setAttribute("alignment-baseline", "middle");
    svgShowingDiffText.setAttribute("font-family", "Arial, sans-serif");
    svgShowingDiffText.setAttribute("font-weight", "bold");
    svgShowingDiffText.setAttribute("pointer-events", "none");
    svgShowingDiffText.setAttribute("text-decoration", "none");
    svgShowingDiffText.setAttribute("text-transform", "none");
    svgShowingDiffText.setAttribute("text-align", "center");

    svgShowingDiffText.textContent = "0";
    svg.appendChild(svgShowingDiff);

    svgShowingDiff.style.display = "none";

    return (x: number, y: number, num: number) => {
      if (x === 0 && y === 0) {
        svgShowingDiff.style.display = "none";
      } else {
        svgShowingDiff.style.display = "block";
        svgShowingDiff.setAttribute("transform", `translate(${x}, ${y})`);
        svgShowingDiffText.textContent = unitsDisplayFn(num);
      }
    };
  }
}

/** This function not only creates the inputs, also mutates the dataStore with those values */
function createBottomBasesInputsForEditing({
  bls,
  offsetBottomBases,
  lcgVcgTcgAndPairings,
  dataStore,
  onInputFocus = () => undefined,
}: {
  bls: IBayLevelData[];
  offsetBottomBases: Array<IBayLevelOffsetBottomBase> | undefined;
  lcgVcgTcgAndPairings: BayLcgVcgTcgAndPairings;
  dataStore: FieldsValuesStore;
  onInputFocus?: (e: Event) => void;
}) {
  const offsetBottomBasesDict = offsetBottomBases
    ? offsetBottomBases.reduce((acc, bb) => {
        acc[`${bb.bays.join("-")}|${bb.level}`] = bb.offset;
        return acc;
      }, {} as Record<string, number>)
    : {};

  const bayLevelPositionsAllDict = createDictionary<BaySizesAndCgs, string>(
    [
      ...lcgVcgTcgAndPairings.bayLevelPositionsAbove,
      ...lcgVcgTcgAndPairings.bayLevelPositionsBelow,
    ],
    (bl) => `${bl.isoBay}-${bl.level}`,
  );

  const pairedBays = getAllThePairedBays(bls, false);
  const blsUniqueKeys = [
    ...pairedBays.pairedBays.map((pb) => `${pb.base}-${pb.paired}|${pb.level}`),
    ...pairedBays.unpairedBays.map((ub) => `${ub.base}|${ub.level}`),
  ].sort();

  const details = document.createElement("div");
  details.className = "side-view-details";

  const h4 = document.createElement("h4");
  h4.innerHTML = getTranslation("view:view3D.sections.adjutstBottomBases");
  details.appendChild(h4);

  const inputsHolder = document.createElement("div");
  inputsHolder.className = "side-view-details-inputs";

  blsUniqueKeys.forEach((bKey) => {
    const customBb = offsetBottomBasesDict[bKey];
    const [baysStr, level] = bKey.split("|");
    const bays = baysStr.split("-") as IBayPattern[];

    const minTier = Math.min(
      bayLevelPositionsAllDict[`${bays[0]}-${level}`]?.minTier || Infinity,
      bayLevelPositionsAllDict[`${bays[1]}-${level}`]?.minTier || Infinity,
    );

    if (minTier === Infinity) return;

    const lcgsBy20OfBay = lcgVcgTcgAndPairings.lcgsBy20Bay[bays[0]];
    const labelBay =
      lcgsBy20OfBay.confirmedPairedBay === undefined
        ? baysStr
        : pad3(
            Math.round(
              (Number(bays[0]) + Number(lcgsBy20OfBay.confirmedPairedBay)) / 2,
            ),
          );

    const input = createInputWithUnits({
      label: getTranslation("view:view3D.sections.adjustBayLevelBB", {
        baysStr: labelBay,
        minTier: pad2(minTier),
        level: getTranslation(
          `enums:BayLevelEnum.${BayLevelEnum[Number(level)]}`,
        ),
      }),
      name: bKey,
      converter: globalUnits.lengthUnits,
      converterPrecision: 3,
      value: customBb,
      placeholder: 0,
      noSpinButtons: true,
      size: "small",
    });

    const smallText = document.createElement("small");
    smallText.slot = "suffix";
    smallText.innerHTML = `tier ${pad2(minTier)}`;
    smallText.style.color = "var(--sl-color-neutral-400)";
    smallText.style.backgroundColor = "var(--sl-color-neutral-100)";
    smallText.style.padding = "0 4px";
    smallText.style.borderRadius = "4px";
    smallText.style.display = "inline-block";
    input.appendToInput(smallText);

    input.setAttribute("id", `b${bKey.replace("|", "_")}`);

    //input.value = dataStore.getValueAsNumber(bKey);
    dataStore.registerInputField(input);

    inputsHolder.appendChild(input);

    input.addEventListener("focus", onInputFocus, false);
  });

  details.appendChild(inputsHolder);
  return details;
}

function attachUpAndDownDraggingActions({
  node,
  svgSizeRatio,
  onDragColor,
  showSizeInfo,
  updateCb,
  onDraggingChange,
}: {
  node: SVGGElement;
  svgSizeRatio: number;
  onDragColor: string;
  showSizeInfo: (x: number, y: number, num: number) => void;
  updateCb: (id: string, diffY: number, initialY: number) => void;
  onDraggingChange: (isDragging: boolean) => void;
}) {
  const dsInitialY = Number(node.dataset.initialY || "0");

  const rectMatrix = node.transform.baseVal.getItem(0).matrix;
  const orginalX = rectMatrix.e;
  const originalY = rectMatrix.f;

  let isDragging = false;
  let startMouseY = 0;
  let startTranslateY = 0;
  let diffMouseY = 0;
  let diffOverallY = 0;

  const rectSvg = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "rect",
  );

  rectSvg.setAttribute("x", "0");
  rectSvg.setAttribute("y", "0");
  rectSvg.setAttribute("width", String(node.getAttribute("data-size-w")));
  rectSvg.setAttribute("height", String(node.getAttribute("data-size-h")));
  rectSvg.setAttribute("fill", onDragColor);
  rectSvg.setAttribute("stroke", onDragColor);
  rectSvg.setAttribute("stroke-width", "0");
  rectSvg.setAttribute("fill-opacity", "0");
  rectSvg.setAttribute("pointer-events", "none");
  node.appendChild(rectSvg);

  // Add an observer of the class of the group to make it look as active
  new MutationObserver((edx) => {
    const mRec = edx[0];
    if (mRec.type !== "attributes" && mRec.attributeName !== "data-active")
      return;

    const isActive = !!node.getAttribute("data-active");
    rectSvg.setAttribute("stroke-width", isActive ? "20" : "0");
  }).observe(node, { attributes: true });

  // This event is to propagate the hover event to the parent bays holder
  node.addEventListener("mouseover", (e) => {
    e.preventDefault();
  });

  node.addEventListener("mousedown", (e) => {
    if (e.button !== 0) return; // Only left button

    startMouseY = e.clientY;
    startTranslateY = node.transform.baseVal.getItem(0).matrix.f;
    isDragging = true;
    onDraggingChange(true);

    assertNonNullish(node.ownerSVGElement, "SVG element not found");
    node.ownerSVGElement.addEventListener("mousemove", onMouseMove);
    node.ownerSVGElement.addEventListener("mouseup", onMouseUp);

    showSizeInfo(
      orginalX + 300,
      originalY,
      -roundDec(diffOverallY / svgSizeRatio, 2) + dsInitialY,
    );

    rectSvg.setAttribute("fill-opacity", "0.4");
  });

  function onMouseMove(e: MouseEvent) {
    if (!isDragging) {
      removeHandlers();
      return;
    }

    diffMouseY = e.clientY - startMouseY;
    diffOverallY = startTranslateY + diffMouseY - originalY;

    node.transform.baseVal
      .getItem(0)
      .setTranslate(orginalX, startTranslateY + diffMouseY);

    showSizeInfo(
      orginalX + 300,
      originalY,
      -roundDec(diffOverallY / svgSizeRatio, 2) + dsInitialY,
    );
  }

  function onMouseUp() {
    isDragging = false;
    onDraggingChange(false);
    const dsId = node.dataset.id || "";
    const dsInitialY = Number(node.dataset.initialY || "0");
    updateCb(dsId, diffOverallY, dsInitialY);
    removeHandlers();
    showSizeInfo(0, 0, 0);
  }

  function removeHandlers() {
    rectSvg.setAttribute("fill-opacity", "0");
    assertNonNullish(node.ownerSVGElement, "SVG element not found");
    node.ownerSVGElement.removeEventListener("mousemove", onMouseMove);
    node.ownerSVGElement.removeEventListener("mouseup", onMouseUp);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface IFormReturn<T extends Record<string, any>> {
  node: HTMLElement;
  submitFunction: (() => boolean) | (() => Promise<boolean>);
  tedivoForm: TedivoForm<T>;
}

function bottomBasesMapsAreEqual(
  map1: Map<string, unknown> | undefined,
  map2: Map<string, unknown> | undefined,
) {
  if (!map1 || !map2) {
    return false;
  }

  if (map1.size !== map2.size) {
    return false;
  }

  for (const [key, val] of map1) {
    const testVal = map2.get(key) || 0;
    // in cases of an undefined value, make sure the key
    // actually exists on the object so there are no false positives
    if (testVal !== (val || 0) || (testVal === undefined && !map2.has(key))) {
      return false;
    }
  }

  return true;
}
